import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import {
  ARR_QUESTIONS,
  PROFILES,
  BOOKING_LINK,
  computeStyle,
  shuffle,
} from "../data/arredamentoData";
import logo from "../assets/logo.webp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, props);
    }
  } catch (e) {
    /* no-op */
  }
};

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.01 2C6.48 2 2 6.48 2 12c0 1.77.46 3.42 1.27 4.86L2 22l5.25-1.38A9.94 9.94 0 0 0 12.01 22C17.54 22 22 17.52 22 12S17.54 2 12.01 2z" />
  </svg>
);
const InstagramIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const ArredamentoQuiz = () => {
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [shuffled, setShuffled] = useState([]);
  const submittedRef = useRef(false);

  const total = ARR_QUESTIONS.length;

  const start = () => {
    track("arredamento_started");
    setShuffled(ARR_QUESTIONS.map((q) => shuffle(q.options)));
    setAnswers({});
    setCurrent(0);
    submittedRef.current = false;
    setPhase("quiz");
  };

  const q = ARR_QUESTIONS[current];
  const opts = shuffled[current] || [];

  const select = (opt) => {
    const next = { ...answers, [q.id]: opt.style };
    setAnswers(next);
    track("arredamento_answer", { question: q.id, style: opt.style });
    setTimeout(() => {
      if (current + 1 < total) setCurrent((c) => c + 1);
      else setPhase("result");
    }, 300);
  };

  const goBack = () => {
    if (current > 0) setCurrent((c) => c - 1);
  };

  const result = phase === "result" ? computeStyle(answers) : null;
  const profile = result && result.style ? PROFILES[result.style] : null;

  useEffect(() => {
    if (phase === "result" && profile && !submittedRef.current) {
      submittedRef.current = true;
      track("arredamento_result", { style: result.style });
      axios
        .post(`${API}/arredamento/submit`, { answers })
        .catch((e) => console.error("arredamento submit failed", e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const perc = Math.round(((current + 1) / total) * 100);

  const shareText = profile
    ? `Il mio stile di arredamento è ${profile.name}! Scopri il tuo`
    : "Scopri il tuo stile di arredamento";
  const shareUrl =
    typeof window !== "undefined" ? window.location.href : "";
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;

  const restart = () => {
    start();
  };

  return (
    <div className="App arr-page">
      {/* Minimal top bar with logo */}
      <div className="arr-topbar">
        <Link to="/">
          <img src={logo} alt="Francesca Spinazzola" className="arr-logo" />
        </Link>
        <Link to="/prodotti" className="arr-topbar-link">
          I nostri strumenti
        </Link>
      </div>

      {phase === "intro" && (
        <section className="arr-intro">
          <p className="section-eyebrow">Il test dello stile</p>
          <h1 className="arr-intro-title serif">Che stile di arredamento sei?</h1>
          <p className="arr-intro-sub">
            Rispondi a 10 domande e scopri lo stile che ti rappresenta, con i
            miei consigli da home stager.
          </p>
          <button className="quiz-cta quiz-cta-primary arr-start" onClick={start}>
            <Sparkles size={17} strokeWidth={1.8} />
            <span>Inizia il test</span>
          </button>
        </section>
      )}

      {phase === "quiz" && (
        <section className="quiz-section arr-quiz-section">
          <div className="quiz-card">
            <div className="quiz-progress">
              <span className="quiz-step-label">
                Domanda {current + 1} di {total}
              </span>
              <span className="quiz-step-perc">{perc}%</span>
            </div>
            <div className="quiz-progress-bar">
              <div className="quiz-progress-fill" style={{ width: `${perc}%` }} />
            </div>

            <div className="quiz-fade" key={current}>
              <h3 className="quiz-question serif">{q.question}</h3>
              <div className="quiz-options">
                {opts.map((opt) => (
                  <button
                    key={opt.style}
                    className={`quiz-option ${
                      answers[q.id] === opt.style ? "selected" : ""
                    }`}
                    onClick={() => select(opt)}
                  >
                    <span className="opt-text">{opt.text}</span>
                  </button>
                ))}
              </div>
              {current > 0 && (
                <button className="quiz-back" onClick={goBack}>
                  <ArrowLeft size={16} strokeWidth={1.8} />
                  Indietro
                </button>
              )}
            </div>
          </div>

          {/* Skip the test -> go to the tools page (with the tabs) */}
          <div className="skip-test">
            <Link to="/prodotti" className="skip-test-btn">
              <span>Salta il test e vai agli strumenti</span>
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </div>
        </section>
      )}

      {phase === "result" && profile && (
        <section className="arr-result">
          <div className="arr-result-head">
            <p className="section-eyebrow">Il tuo stile è</p>
            <h1 className="arr-result-name serif">{profile.name}</h1>
          </div>

          <img
            src={profile.image}
            alt={`Stile ${profile.name}`}
            className="arr-result-img"
            loading="lazy"
          />

          <div className="arr-result-body">
            <p className="arr-desc">{profile.description}</p>

            <div className="arr-block">
              <h4 className="arr-block-title">Caratteristiche chiave</h4>
              <p>{profile.caratteristiche}</p>
            </div>
            <div className="arr-block">
              <h4 className="arr-block-title">A chi è adatto</h4>
              <p>{profile.perChi}</p>
            </div>
            <div className="arr-block arr-tip">
              <h4 className="arr-block-title">Il consiglio della home stager</h4>
              <p>{profile.consiglio}</p>
            </div>

            {/* CTA */}
            <div className="arr-cta-box">
              <p className="arr-cta-text">
                Vuoi trasformare il tuo stile in più prenotazioni? Prenota una
                chiamata gratuita con me: insieme daremo alla tua struttura il
                valore che merita.
              </p>
              <a
                href={BOOKING_LINK}
                className="quiz-cta quiz-cta-primary"
                onClick={() =>
                  track("arredamento_call_click", { style: result.style })
                }
              >
                <span>Prenota la chiamata gratuita</span>
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
            </div>

            <Link
              to="/prodotti"
              className="quiz-cta quiz-cta-ghost arr-products-btn"
              onClick={() => track("arredamento_products_click")}
            >
              <span>Scopri i nostri prodotti</span>
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>

            {/* Share */}
            <div className="arr-share">
              <span className="arr-share-label">Condividi il risultato</span>
              <div className="arr-share-btns">
                <a
                  className="arr-share-btn wa"
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("arredamento_share", { ch: "whatsapp" })}
                >
                  <WhatsAppIcon /> WhatsApp
                </a>
                <a
                  className="arr-share-btn ig"
                  href="https://www.instagram.com/francescaspinazzola/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("arredamento_share", { ch: "instagram" })}
                >
                  <InstagramIcon /> Instagram
                </a>
              </div>
            </div>

            <button className="quiz-back arr-restart" onClick={restart}>
              <RotateCcw size={15} strokeWidth={1.8} />
              Rifai il test
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArredamentoQuiz;
